class AiRecommendations extends HTMLElement {
  constructor() {
    super();
    this.container = this.querySelector('.ai-recommendations-container');
    this.loading = this.querySelector('.ai-recommendations-loading');
    this.handle = this.dataset.handle;
    this.tenant = this.dataset.tenant;

    // Configurable base URL from Liquid dataset
    this.baseUrl = this.dataset.apiUrl || 'https://api.productadvisorai.com';

    this.observer = new IntersectionObserver(this.handleIntersection.bind(this), {
      rootMargin: '0px 0px 400px 0px' // Load slightly before it comes into view
    });
  }

  connectedCallback() {
    if (!this.handle) return;

    // Load recommendations immediately so they are ready before
    // the customer scrolls to this section.
    this.fetchRecommendations();

    // Listen for Shopify variant URL changes
    this.setupVariantListener();
  }

  disconnectedCallback() {
    this.observer.disconnect();
    window.removeEventListener('ai-variant-change', this.onVariantChange);
  }

  setupVariantListener() {
    this.onVariantChange = () => {
      if (this.hasLoaded) {
        // Show loading state again
        this.container.innerHTML = '';
        this.loading.style.display = 'flex';
        this.fetchRecommendations();
      }
    };

    // Attach to our custom event
    window.addEventListener('ai-variant-change', this.onVariantChange);

    // Intercept pushState and replaceState globally (only once)
    if (!window._aiHistoryIntercepted) {
      const triggerEvent = () => window.dispatchEvent(new Event('ai-variant-change'));

      const originalPushState = history.pushState;
      history.pushState = function () {
        originalPushState.apply(this, arguments);
        triggerEvent();
      };

      const originalReplaceState = history.replaceState;
      history.replaceState = function () {
        originalReplaceState.apply(this, arguments);
        triggerEvent();
      };

      window.addEventListener('popstate', triggerEvent);
      window._aiHistoryIntercepted = true;
    }
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;

    observer.unobserve(this);
    this.fetchRecommendations();
  }

  async fetchRecommendations() {
    try {
      // 1. Get the current Shopify variant ID from the URL or fallback to initial
      const urlParams = new URLSearchParams(window.location.search);
      const shopifyVariantId = urlParams.get('variant') || this.dataset.initialVariant;

      // 2. We need the SKU for our backend database, not the Shopify numeric ID!
      let targetSku = this.dataset.initialVariantSku; // Fallback to initial SKU

      // If the variant in the URL is different from the initial load, fetch the product JSON to get its SKU
      if (shopifyVariantId && shopifyVariantId !== this.dataset.initialVariant) {
        try {
          const productRes = await fetch(`${window.location.pathname}.js`);
          if (productRes.ok) {
            const productJson = await productRes.json();
            const matchingVariant = productJson.variants.find(v => v.id.toString() === shopifyVariantId.toString());
            if (matchingVariant && matchingVariant.sku) {
              targetSku = matchingVariant.sku;
            }
          }
        } catch (e) {
          console.warn('[AI Recommendations] Failed to fetch variant SKU', e);
        }
      }

      let url = `${this.baseUrl}/api/recommendations/pdp?tenant_id=${encodeURIComponent(this.tenant)}&product_handle=${encodeURIComponent(this.handle)}`;

      // Send the SKU to the backend because our database is keyed by SKU!
      if (targetSku) {
        url += `&variant_id=${encodeURIComponent(targetSku)}`;
      }

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`API returned status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.sections || data.sections.length === 0) {
        this.showEmptyState();
        return;
      }

      this.render(data.sections);
      this.hasLoaded = true;

      // Fire impression event
      this.trackEvent('impression', null, null);

    } catch (error) {
      console.error('[AI Recommendations] Failed to fetch:', error);
      this.showEmptyState();
    }
  }

  showEmptyState() {
    this.loading.style.display = 'none';
    this.style.display = 'none';
  }

  formatMoney(cents) {
    // Basic formatter; assume API returns raw dollars/floats or cents? 
    // Wait, API returns floats (e.g. 71.6). So we just format as currency.
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents);
  }

  render(sections) {
    this.loading.style.display = 'none';
    this.container.innerHTML = ''; // Clear previous recommendations

    const sectionTemplate = document.getElementById('ai-section-template');
    const cardTemplate = document.getElementById('ai-card-template');

    if (!sectionTemplate || !cardTemplate) {
      console.error('[AI Recommendations] Missing HTML templates');
      return;
    }

    // Shopify responsive grid classes based on section settings
    const colsDesktop = this.dataset.columnsDesktop || 4;
    const colsMobile = this.dataset.columnsMobile || 2;
    const gridClass = `grid--${colsDesktop}-col-desktop grid--${colsMobile}-col-tablet-down`;

    sections.forEach(section => {
      if (!section.recommendations || section.recommendations.length === 0) return;

      // Clone section template
      const sectionNode = sectionTemplate.content.cloneNode(true);
      const sectionContainer = sectionNode.querySelector('.ai-recommendations-section');
      const titleEl = sectionNode.querySelector('.ai-section-title');
      const gridEl = sectionNode.querySelector('.ai-section-grid');

      titleEl.textContent = section.title;
      gridEl.className = `grid product-grid ai-section-grid ${gridClass}`;

      // Clone cards
      section.recommendations.forEach(rec => {
        const cardNode = cardTemplate.content.cloneNode(true);

        // Populate Image
        const img = cardNode.querySelector('.ai-product-image');
        if (rec.image_url) {
          const originalSrc = rec.image_url;
          const shopifyHandle = rec.handle;
          
          if (window.CloudImageHandler && window.CloudImageHandler.optimizeImageUrl) {
            img.src = window.CloudImageHandler.optimizeImageUrl(originalSrc);
            img.setAttribute('data-current-url', originalSrc);
          } else {
            img.src = originalSrc;
          }
          
          img.alt = rec.title;
          img.onerror = function () {
            if (window.CloudImageHandler) {
              const fakeImagesArray = btoa(JSON.stringify([{url: originalSrc}]));
              window.CloudImageHandler.onError(this, fakeImagesArray, shopifyHandle, rec.title);
            } else {
              this.src = 'https://cdn.shopify.com/s/images/themes/product-1.png'; // fallback
            }
          };
        }

        // Populate Title and Link
        const titleSpan = cardNode.querySelector('.ai-product-title');
        const linkEl = cardNode.querySelector('.ai-product-link');
        titleSpan.textContent = rec.title;
        // Construct standard shopify product URL
        linkEl.href = `/products/${rec.handle}`;

        // Asynchronously resolve the numeric variant ID from Shopify so it pre-selects on the PDP
        if (rec.variant_id) {
          fetch(`/products/${rec.handle}.js`)
            .then(res => res.json())
            .then(prodData => {
              if (prodData && prodData.variants) {
                const matchedVariant = prodData.variants.find(v =>
                  v.sku === rec.variant_id ||
                  (v.id && String(v.id) === String(rec.variant_id))
                );
                if (matchedVariant && matchedVariant.id) {
                  linkEl.href = `/products/${rec.handle}?variant=${matchedVariant.id}`;
                }
              }
            })
            .catch(err => { /* fail silently, defaults to parent product URL */ });
        }

        // Attach click tracking
        linkEl.addEventListener('click', () => {
          this.trackEvent('click', rec.handle, rec.rank, rec.rec_type);
        });

        // Populate Vendor
        const vendorEl = cardNode.querySelector('.ai-product-vendor');
        if (rec.vendor) {
          vendorEl.textContent = rec.vendor;
        }

        // Populate Size Swatch
        const sizeSwatchEl = cardNode.querySelector('.ai-product-size-swatch');
        if (rec.size) {
          sizeSwatchEl.textContent = rec.size;
          const swatchContainer = cardNode.querySelector('.ai-product-swatches-container');
          if (swatchContainer) swatchContainer.style.display = 'block';
        }

        // Populate Pricing
        const regPriceEl = cardNode.querySelector('.price__regular');
        const salePriceEl = cardNode.querySelector('.price__sale');

        if (rec.compare_at_price && rec.compare_at_price > rec.price) {
          regPriceEl.style.display = 'none';
          salePriceEl.style.display = 'block';

          cardNode.querySelector('.price-item--sale').textContent = this.formatMoney(rec.price);
          cardNode.querySelector('.ai-product-compare-price').textContent = this.formatMoney(rec.compare_at_price);

          // Show sale badge (or free shipping badge)
          const badge = cardNode.querySelector('.ai-badge');
          badge.style.display = 'block';
          const percentOff = Math.round(((rec.compare_at_price - rec.price) / rec.compare_at_price) * 100);
          badge.textContent = `SAVE ${percentOff}%`;
        } else {
          regPriceEl.style.display = 'block';
          salePriceEl.style.display = 'none';
          cardNode.querySelector('.price-item--regular.ai-product-price').textContent = this.formatMoney(rec.price);
        }

        gridEl.appendChild(cardNode);
      });

      this.container.appendChild(sectionNode);
    });
  }

  trackEvent(eventType, candidateHandle, rank, recType) {
    if (!navigator.sendBeacon) return;

    const url = `${this.baseUrl}/api/recommendations/events`;

    const visitorId = window.AnalyticsContext ? window.AnalyticsContext.getVisitorId() : (this.getCookie('_shopify_y') || 'anonymous');
    const sessionId = window.AnalyticsContext ? window.AnalyticsContext.getSessionId() : null;
    const requestId = window.AnalyticsContext ? window.AnalyticsContext.generateRequestId() : null;

    const payload = {
      event_type: eventType === 'impression' ? 'recommendation_impression' : 'recommendation_click',
      tenant_id: this.tenant,
      source_handle: this.handle,
      visitor_id: visitorId,
      session_id: sessionId,
      request_id: requestId
    };

    if (candidateHandle) {
      payload.candidate_handle = candidateHandle;
      payload.rank = rank;
      if (recType) {
        payload.rec_type = recType;
      }
    }

    // sendBeacon requires FormData, Blob, or URLSearchParams for JSON bodies
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    navigator.sendBeacon(url, blob);
  }

  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }
}

customElements.define('ai-recommendations', AiRecommendations);
